import { Modal } from '../ui/modal.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';

const REASON_LABELS = {
  unspecified:          'Unspecified',
  keyCompromise:        'Key compromise',
  caCompromise:         'CA compromise',
  affiliationChanged:   'Affiliation changed',
  superseded:           'Superseded',
  cessationOfOperation: 'Cessation of operation',
  certificateHold:      'Certificate hold',
  privilegeWithdrawn:   'Privilege withdrawn',
};

export function CertDetailModal({ open, certificate, viewerTimezone, onClose, downloadHref }) {
  if (!open || !certificate) return null;
  const c = certificate;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={c.subjectCn}
      description={c.status === 'revoked' ? 'Revoked certificate' : c.isExpired ? 'Expired certificate' : 'Active certificate'}
      size="lg"
      footer={
        <>
          <a href={downloadHref} download={`${c.subjectCn}.crt`} className="btn-secondary" data-skip-autofocus>
            <i className="fa-solid fa-download" aria-hidden="true" /> Download cert
          </a>
          <button type="button" onClick={onClose} className="btn-primary">
            Close
          </button>
        </>
      }
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Subject CN" mono>{c.subjectCn}</Field>
        <Field label="Status">
          {c.status === 'revoked'
            ? `Revoked${c.revocationReason ? ` (${REASON_LABELS[c.revocationReason] || c.revocationReason})` : ''}`
            : c.isExpired ? 'Expired' : 'Active'}
        </Field>
        <Field label="Serial" mono full>
          <span className="break-all text-[12px]">{c.serial}</span>
        </Field>
        <Field label="Fingerprint (SHA-256)" mono full>
          <span className="break-all text-[11px]">{c.fingerprintSha256}</span>
        </Field>
        <Field label="Algorithm">{c.algorithm}</Field>
        <Field label="Issued at">{formatUtcInTimezone(c.notBefore, viewerTimezone)}</Field>
        <Field label="Expires at">{formatUtcInTimezone(c.notAfter, viewerTimezone)}</Field>
        <Field label="Issued (record)">{formatUtcInTimezone(c.createdAt, viewerTimezone)}</Field>
        {c.revokedAt && (
          <Field label="Revoked at">{formatUtcInTimezone(c.revokedAt, viewerTimezone)}</Field>
        )}
      </dl>

      {(c.sanDns?.length > 0 || c.sanUri?.length > 0) && (
        <div className="mt-5">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-mist">Subject alternative names</div>
          <div className="flex flex-wrap gap-1.5">
            {c.sanDns?.map((d) => (
              <span key={`d-${d}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 font-mono text-[11px] text-ink">
                <i className="fa-solid fa-globe text-mist text-[9px]" aria-hidden="true" />
                {d}
              </span>
            ))}
            {c.sanUri?.map((u) => (
              <span key={`u-${u}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 font-mono text-[11px] text-ink">
                <i className="fa-solid fa-link text-mist text-[9px]" aria-hidden="true" />
                {u}
              </span>
            ))}
          </div>
        </div>
      )}

      {c.notes && (
        <div className="mt-5">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-mist">Notes</div>
          <p className="rounded-2xl border border-line bg-canvas px-4 py-3 text-sm text-ink whitespace-pre-wrap">
            {c.notes}
          </p>
        </div>
      )}
    </Modal>
  );
}

function Field({ label, children, mono, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</dt>
      <dd className={`mt-0.5 text-sm ${mono ? 'font-mono text-ink' : 'text-ink'}`}>{children}</dd>
    </div>
  );
}
